import React, { useState } from "react";
import type { MenuProps } from "antd";
import { Image, Layout, Menu, Tag, theme } from "antd";
import { Link, Outlet } from "react-router-dom";
import { isLoggedIn } from "../../services/authService";
import { isDemoMode } from "../../config/demoConfig";
import logo from "../../assets/logo.png";
import { useAuth } from "../../contexts/authContext";
import { sidebar } from "../../routes";
import UserMenu from "./UserMenu";
import { UserDTO } from "../../models/User";
import NotificationMenu from "./NotificationMenu";
import CustomFooter from "./CustomFooter";
import DemoFooter from "./DemoFooter";
import styles from "./ProtectedLayout.module.css";

const { Header, Content, Footer, Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[]
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}

const checkCapabilities = (
  loggedUser: UserDTO | null, // creare il modello dell'utente loggato
  capabilities?: string[]
) => {
  if (!loggedUser) return false;
  const isAdmin = loggedUser?.authorities?.some((a) => a.name === "ROLE_ADMIN");
  if (isAdmin) return true;

  if (!capabilities || capabilities.length === 0) return false;
  const hasRequiredCapability = capabilities?.some((capability) =>
    loggedUser?.authorities?.some((authority) => authority.name === capability)
  );
  if (!isAdmin && !hasRequiredCapability) return false;

  return true;
};

interface IProtectedLayout {
  hideSider?: boolean;
}

const ProtectedLayout = ({ hideSider }: IProtectedLayout) => {
  const { loggedUser } = useAuth();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const items: MenuItem[] = sidebar
    .filter((s) => checkCapabilities(loggedUser, s.capabilities))
    .map((s) => {
      return getItem(
        !s.childrens || s.childrens?.length === 0 ? (
          <Link key={s.name} to={s.route!}>
            {s.name}
          </Link>
        ) : (
          <div>{s.name}</div>
        ),
        s.name,
        <s.icon />,
        s.childrens && s.childrens.length > 0
          ? s.childrens.map((child) =>
              getItem(
                <Link key={`${child.name}`} to={`${child.route}`}>
                  {child.name}
                </Link>,
                `${child.route}`
              )
            )
          : undefined
      );
    });

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {!hideSider && (
        <Sider
          collapsible
          collapsed={collapsed}
          breakpoint="lg"
          collapsedWidth="0"
          className={styles.sider}
          onCollapse={(value) => setCollapsed(value)}
        >
          <div
            style={{
              height: "32px",
              margin: !collapsed ? "16px" : "16px 5px",
              background: "#F5F5F5",
              borderRadius: "6px",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Image preview={false} src={logo} width={30} />
          </div>
          <Menu
            theme="dark"
            defaultSelectedKeys={["1"]}
            mode="inline"
            items={items}
          />
        </Sider>
      )}
      <Layout>
        <Header
          className={styles.header}
          style={{ background: colorBgContainer }}
        >
          <Tag color="red" style={{ fontSize: "large" }} className={styles.demoTag}>
            DEMO
          </Tag>
          {isLoggedIn() && (
            <>
              <NotificationMenu />
              <div className={styles.spacer} />
              <UserMenu />
            </>
          )}
        </Header>
        <Content className={styles.content}>
          <Outlet />
        </Content>
        {isDemoMode ? <DemoFooter /> : <CustomFooter />}
      </Layout>
    </Layout>
  );
};

export default ProtectedLayout;
